import React from 'react';
import { enUS } from 'date-fns/locale';
import styled, { createGlobalStyle } from 'styled-components';

import {
  DateRangePicker as NiceDateRangePicker,
  START_DATE,
  END_DATE,
} from '@axel-dev/react-nice-dates';
import '@axel-dev/react-nice-dates/build/style.css';

import { Container, TextInput, ClearIcon } from 'ui/components/styled';
import { spacing } from 'ui/theme/utils';
import _ from 'lodash';

export const DateRangePicker = ({
  width = '200px',
  onChange,
  format,
  startDate,
  endDate,
  setStartDate,
  setEndDate,
  clearValue = '',
  isClearable,
  ...rest
}) => {
  const startDateFocused = React.useRef(false);
  const endDateFocused = React.useRef(false);

  const handleClear = () => {
    onChange(clearValue);
  };

  const handleStartInputFocus = (event, callback) => {
    startDateFocused.current = true;
    callback(event);
  };

  const handleStartInputBlur = (event, callback) => {
    startDateFocused.current = false;
    callback(event);
  };

  const handleEndInputFocus = (event, callback) => {
    endDateFocused.current = true;
    callback(event);
  };

  const handleEndInputBlur = (event, callback) => {
    endDateFocused.current = false;
    callback(event);
  };

  const startDateValue = _.isDate(startDate) ? startDate : null;
  const endDateValue = _.isDate(endDate) ? endDate : null;

  return (
    <>
      <DatePickerStyle />
      <NiceDateRangePicker
        startDate={startDateValue}
        endDate={endDateValue}
        onStartDateChange={setStartDate}
        onEndDateChange={setEndDate}
        format={format}
        locale={enUS}
      >
        {({ startDateInputProps, endDateInputProps, focus }) => (
          <Container flexDirection="row" gap="10px">
            <RelativeContainer {...rest} width={width} height="auto">
              <CalendarIcon />
              <TextInput
                className={'input' + (focus === START_DATE ? ' -focused' : '')}
                variant="minimal"
                textAlign="center"
                cursor="initial"
                px="40px"
                {...startDateInputProps}
                placeholder="Start Date"
                onFocus={(event) => handleStartInputFocus(event, startDateInputProps.onFocus)}
                onBlur={(event) => handleStartInputBlur(event, startDateInputProps.onBlur)}
              />
              {isClearable && startDateValue && <ClearIcon onClick={handleClear} />}
            </RelativeContainer>
            <RelativeContainer {...rest} width={width} height="auto">
              <CalendarIcon />
              <TextInput
                className={'input' + (focus === END_DATE ? ' -focused' : '')}
                variant="minimal"
                textAlign="center"
                cursor="initial"
                px="40px"
                {...endDateInputProps}
                placeholder="End Date"
                onFocus={(event) => handleEndInputFocus(event, endDateInputProps.onFocus)}
                onBlur={(event) => handleEndInputBlur(event, endDateInputProps.onBlur)}
              />
              {isClearable && endDateValue && <ClearIcon onClick={handleClear} />}
            </RelativeContainer>
          </Container>
        )}
      </NiceDateRangePicker>
    </>
  );
};

const DatePickerStyle = createGlobalStyle`
  .nice-dates-day {
    &::before {
      background-color: ${({ theme }) => theme.colors.primary};
    }
    &::after {
      border: none;
      background-color: #e7edff;
    }
`;

const RelativeContainer = styled(Container)`
  position: relative;
  flex-direction: row;
  gap: 10px;
  ${(props) => spacing(props)}
`;

const CalendarIcon = styled.img.attrs(() => ({
  src: '/assets/img/icons/calendar.svg',
  alt: 'calendar',
}))`
  position: absolute;
  top: 8px;
  left: 10px;
  width: 18px;
  pointer-events: none;
`;
